import {
  WorkerRequirementMappingLevel,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { isRequirementInstantReview } from "@src/appV2/redesign/Documents/utils/isRequirementInstantReview";
import { isAfter, parseISO } from "date-fns";

import { type WorkerRequirement } from "../../../Documents/api/types";
import { type BlockingRequirementsContext } from "./types";

interface GetBlockingRequirementsContextProps {
  blockingRequirements: WorkerRequirement[];
  credentialRequirementConfiguration: Set<string>;
  expiryDate?: string;
}

export function getBlockingRequirementsContext(
  props: GetBlockingRequirementsContextProps
): BlockingRequirementsContext {
  const { blockingRequirements, credentialRequirementConfiguration, expiryDate } = props;
  const visibleBlockingRequirements: WorkerRequirement[] = [];
  const invisibleBlockingRequirements: WorkerRequirement[] = [];
  const inReviewVisibleBlockingRequirements: WorkerRequirement[] = [];
  const notInReviewVisibleBlockingRequirements: WorkerRequirement[] = [];
  const instantReviewVisibleBlockingRequirements: WorkerRequirement[] = [];
  const expiringSoonVisibleBlockingRequirements: WorkerRequirement[] = [];
  const baseCredentialRequirements: WorkerRequirement[] = [];
  const workplaceCredentialRequirements: WorkerRequirement[] = [];

  blockingRequirements.sort((a, b) => {
    return (a.requirement?.id ?? "").localeCompare(b.requirement?.id ?? "");
  });

  blockingRequirements.forEach((requirement) => {
    if (!requirement.requirement?.attributes.visibleToHCP) {
      invisibleBlockingRequirements.push(requirement);
      return;
    }

    if (credentialRequirementConfiguration.has(requirement.requirementId)) {
      if (requirement.level === WorkerRequirementMappingLevel.STATE) {
        baseCredentialRequirements.push(requirement);
      } else if (requirement.level === WorkerRequirementMappingLevel.HCF) {
        workplaceCredentialRequirements.push(requirement);
      }
      return;
    }

    const isExpiringSoonByStatus = requirement.status === WorkerRequirementStatus.EXPIRING;
    const isExpiringSoonByDate =
      isDefined(expiryDate) &&
      isDefined(requirement.expiry) &&
      [
        WorkerRequirementStatus.EXPIRING,
        WorkerRequirementStatus.MISSING,
        WorkerRequirementStatus.APPROVED,
      ].includes(requirement.status)
        ? isAfter(parseISO(expiryDate), parseISO(requirement.expiry))
        : false;

    const isExpiringSoon = isExpiringSoonByStatus || isExpiringSoonByDate;

    const updatedRequirement = isExpiringSoon
      ? { ...requirement, status: WorkerRequirementStatus.EXPIRING }
      : requirement;

    if (requirement.status === WorkerRequirementStatus.IN_REVIEW) {
      inReviewVisibleBlockingRequirements.push(updatedRequirement);
    } else {
      if (isRequirementInstantReview(requirement)) {
        instantReviewVisibleBlockingRequirements.push(updatedRequirement);
      }

      if (isExpiringSoon) {
        expiringSoonVisibleBlockingRequirements.push(updatedRequirement);
      }

      notInReviewVisibleBlockingRequirements.push(updatedRequirement);
    }

    visibleBlockingRequirements.push(updatedRequirement);
  });

  const areAllVisibleBlockedDocumentsInstantReview =
    instantReviewVisibleBlockingRequirements.length > 0 &&
    instantReviewVisibleBlockingRequirements.length === visibleBlockingRequirements.length;

  const areAllVisibleBlockedDocumentsExpiringSoon =
    expiringSoonVisibleBlockingRequirements.length > 0 &&
    expiringSoonVisibleBlockingRequirements.length === visibleBlockingRequirements.length;

  const areAllVisibleBlockedDocumentsInReview =
    inReviewVisibleBlockingRequirements.length > 0 &&
    inReviewVisibleBlockingRequirements.length === visibleBlockingRequirements.length;

  return {
    blockingRequirements,
    hasBlockingRequirements: blockingRequirements.length > 0,
    baseCredentialRequirements,
    workplaceCredentialRequirements,

    visibleBlockingRequirements,
    invisibleBlockingRequirements,

    inReviewVisibleBlockingRequirements,
    notInReviewVisibleBlockingRequirements,

    instantReviewVisibleBlockingRequirements,

    areAllVisibleBlockedDocumentsInstantReview,
    areAllVisibleBlockedDocumentsExpiringSoon,
    areAllVisibleBlockedDocumentsInReview,
  };
}
